import { Box, CircularProgress, Collapse, IconButton, Tooltip, Typography } from "@mui/material";
import { TheoryExamSimulationResult } from "../providers/theoryExamSimulationResultsProvider";
import { useGetManyReference, useGetRecordId } from "react-admin";
import { useDialog } from "../hooks/useDialog";
import { TheoryExamSimulationResultsDialog } from "../dialogs/TheoryExamSimulationResultsDialog";
import { useMemo, useState } from "react";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Row } from "../misc/Row";
import { Column } from "../misc/Column";
import { TheoryExamSimulationsChart } from "../details/student/TheoryExamSimulationsChart";
import { CheckedIcon } from "../icons/CheckedIcon";
import { CircleFalseIcon } from "../icons/CircleFalseIcon";
import { autovioColors } from "../misc/backofficeTheme";

const chartItemCount = 10;

export function StudentTheoryExamSimulationsField(props: { emptyText?: string }) {
  const { emptyText } = props;
  const studentId = useGetRecordId();
  const { dialogProps, openDialog } = useDialog("TheoryExamSimulationResultsDialog");
  const [isChartExpanded, setIsChartExpanded] = useState(false);
  const { data, total } = useGetManyReference<TheoryExamSimulationResult>("theoryExamSimulationResults", {
    target: "studentId",
    id: studentId,
    pagination: { page: 1, perPage: 0 },
    sort: { field: "timestamp", order: "DESC" }, // ... so that the most recent results are on the first page
  });

  const passFailCounts = useMemo(
    () => ({
      passed: data?.filter((r) => r.passed).length ?? 0,
      failed: data?.filter((r) => !r.passed).length ?? 0,
    }),
    [data],
  );

  if (!data || typeof total !== "number") {
    return <CircularProgress size={20} aria-label="Prüfungssimulationen werden geladen" />;
  }
  if (total === 0) {
    return emptyText ? <Typography variant="body2">{emptyText}</Typography> : null;
  }
  return (
    <>
      <Row gap="4px" sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Column sx={{ flex: 1 }}>
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>Prüfungssimulationen ({total})</Typography>
          <Row gap={1.5} sx={{ alignItems: "center" }}>
            <Tooltip title="Bestanden">
              <Box
                aria-label={`${passFailCounts.passed} bestanden`}
                sx={{ alignItems: "center", display: "flex", gap: 0.5 }}
              >
                <CheckedIcon aria-hidden sx={{ color: autovioColors.green, fontSize: 16 }} />
                <Typography sx={{ color: "text.primary", fontSize: 14 }}>{passFailCounts.passed}</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Nicht bestanden">
              <Box
                aria-label={`${passFailCounts.failed} nicht bestanden`}
                sx={{ alignItems: "center", display: "flex", gap: 0.5 }}
              >
                <CircleFalseIcon aria-hidden sx={{ color: autovioColors.red, fontSize: 16 }} />
                <Typography sx={{ color: "text.primary", fontSize: 14 }}>{passFailCounts.failed}</Typography>
              </Box>
            </Tooltip>
          </Row>
        </Column>
        <Row>
          <Tooltip title="Prüfungssimulationen anzeigen">
            <IconButton aria-label="Prüfungssimulationen anzeigen" onClick={openDialog} size="small">
              <FormatListBulletedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={isChartExpanded ? "Infografik ausblenden" : "Infografik anzeigen"}>
            <IconButton
              aria-controls="theory-exam-simulations-chart"
              aria-expanded={isChartExpanded}
              aria-label={isChartExpanded ? "Infografik ausblenden" : "Infografik anzeigen"}
              onClick={() => setIsChartExpanded((expanded) => !expanded)}
              size="small"
            >
              <ExpandMoreIcon
                sx={{
                  transform: isChartExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms",
                }}
              />
            </IconButton>
          </Tooltip>
        </Row>
      </Row>
      <Collapse
        in={isChartExpanded}
        timeout="auto"
        unmountOnExit
        sx={{ width: "100%", "& .MuiCollapse-wrapperInner": { width: "100%" } }}
      >
        <Box id="theory-exam-simulations-chart" sx={{ pt: 1 }}>
          <TheoryExamSimulationsChart chartItemCount={chartItemCount} data={data.slice(0, chartItemCount)} />
        </Box>
      </Collapse>
      <TheoryExamSimulationResultsDialog {...dialogProps} />
    </>
  );
}
